import '@apps/web/features/phoebe_chat_v3/ui/primitives/chat_motion.css';

import { Check, Copy } from 'lucide-react';
import { type ReactNode, useContext, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Markdown } from '@apps/web/components/ui/markdown';
import {
    getAvatarInitials,
    getConversationAvatarToneClass,
} from '@apps/web/components/ui/user_avatar';
import { useEntityReferenceLabelReader } from '@apps/web/features/phoebe_chat_v3/api/entity_references';
import {
    messagePlainText,
    rememberCopiedMessage,
} from '@apps/web/features/phoebe_chat_v3/model/message_clipboard';
import { referenceTokensToMarkdown } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import { type RegistryOccurrence } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { useHarnessScope } from '@apps/web/features/phoebe_chat_v3/ui/harness_scope_context';
import {
    type AssistantActionsVisibility,
    useAssistantActionsVisibility,
} from '@apps/web/features/phoebe_chat_v3/ui/latest_assistant_message_context';
import { MessagePresentationsContext } from '@apps/web/features/phoebe_chat_v3/ui/message_attachments';
import {
    useAssistantTurnPosition,
    useMessageContinuation,
} from '@apps/web/features/phoebe_chat_v3/ui/message_continuation_context';
import { NextStepsOfferContext } from '@apps/web/features/phoebe_chat_v3/ui/next_steps_offer_context';
import { AssistantAvatar } from '@apps/web/features/phoebe_chat_v3/ui/primitives/assistant_avatar';
import {
    AssistantMessageActions,
    MESSAGE_ACTION_BUTTON_CLASS,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/assistant_message_actions';
import { USER_MESSAGE_BUBBLE_CLASSES } from '@apps/web/features/phoebe_chat_v3/ui/primitives/message_bubble_classes';
import { NextStepButtons } from '@apps/web/features/phoebe_chat_v3/ui/primitives/next_step_buttons';
import {
    REFERENCE_MARKDOWN_COMPONENTS,
    ReferenceText,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import { useViewerUserId } from '@apps/web/features/phoebe_chat_v3/ui/viewer_user_context';
import { cn } from '@apps/web/lib/utils';

type ChatTextMessageProps = Readonly<{
    readonly text: string;
    readonly occurrence: RegistryOccurrence<'assistant_text' | 'user_message'>;
    readonly organizationTimeZone: string;
}>;

function occurrenceCreatedAt(
    occurrence: ChatTextMessageProps['occurrence'],
): string | null {
    return (
        occurrence.committed?.created_at ??
        occurrence.pending?.visible_at ??
        null
    );
}

function formatMessageTime(
    occurrence: ChatTextMessageProps['occurrence'],
    organizationTimeZone: string,
): string {
    const createdAt = occurrenceCreatedAt(occurrence);
    if (createdAt === null) {
        return 'Just now';
    }
    return new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: organizationTimeZone,
    }).format(new Date(createdAt));
}

const REVEAL_ON_HOVER_CLASS =
    'opacity-0 group-hover/message:opacity-100 group-focus-within/message:opacity-100';

const MESSAGE_TIME_CLASS = 'text-v3-chat-meta tabular-nums';

function CopyMessageButton({ text }: Readonly<{ readonly text: string }>) {
    const [copied, setCopied] = useState(false);
    const scope = useHarnessScope();
    const labelFor = useEntityReferenceLabelReader(scope);

    function copyMessage(): void {
        void navigator.clipboard
            .writeText(
                scope === null
                    ? messagePlainText(text, labelFor)
                    : rememberCopiedMessage(text, labelFor, scope),
            )
            .then(() => setCopied(true))
            .catch(() => undefined);
    }

    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={copied ? 'Message copied' : 'Copy message'}
            className={MESSAGE_ACTION_BUTTON_CLASS}
            onClick={copyMessage}
        >
            {copied ? (
                <Check aria-hidden="true" className="size-3.5" />
            ) : (
                <Copy aria-hidden="true" className="size-3.5" />
            )}
        </Button>
    );
}

function MessageActionRow({
    label,
    align,
    visibility,
    children,
}: Readonly<{
    readonly label: string;
    readonly align: 'start' | 'end';
    readonly visibility: AssistantActionsVisibility;
    readonly children: ReactNode;
}>): ReactNode {
    return (
        <div
            aria-label={label}
            className={cn(
                'text-muted-foreground mt-1 flex h-6 items-center gap-0.5 transition-opacity',
                align === 'start' ? '-ml-1.5' : '-mr-1.5 justify-end',
                visibility === 'hover' && REVEAL_ON_HOVER_CLASS,
                visibility === 'hidden' && 'invisible',
            )}
        >
            {children}
        </div>
    );
}

export function AssistantTextMessage({
    text,
    occurrence,
    organizationTimeZone,
}: ChatTextMessageProps): ReactNode {
    const { continuesPrevious, continuedByNext, followedByCard } =
        useMessageContinuation();
    const turnPosition = useAssistantTurnPosition();
    const presentations = useContext(MessagePresentationsContext);
    const nextStepsOffer = useContext(NextStepsOfferContext);
    const formattedTime = formatMessageTime(occurrence, organizationTimeZone);
    const actionsVisibility = useAssistantActionsVisibility(
        occurrence.occurrenceId,
    );
    return (
        // No pb on the article: the trailing actions row already closes it,
        // and the virtualizer row adds the inter-message padding.
        <article
            aria-label="Phoebe response"
            className={cn(
                'group/message flex min-w-0 items-start gap-[9px]',
                continuesPrevious && 'pl-[37px]',
            )}
        >
            {!continuesPrevious && <AssistantAvatar />}
            <div className="w-full max-w-[65ch] min-w-0">
                {!continuesPrevious && (
                    <div className="mb-1.5 flex items-baseline gap-[7px]">
                        <strong className="text-foreground text-v3-chat-meta font-[650]">
                            Phoebe
                        </strong>
                    </div>
                )}
                <Markdown
                    components={REFERENCE_MARKDOWN_COMPONENTS}
                    className="text-foreground [&_code]:text-v3-chat-meta [&_h1]:text-v3-chat [&_h2]:text-v3-chat [&_h3]:text-v3-chat [&_li]:text-v3-chat [&_p]:text-v3-chat [&_td]:text-v3-chat [&_th]:text-v3-chat-meta [overflow-wrap:anywhere] break-words [&_h1]:font-bold [&_h2]:font-bold [&_h3]:font-bold [&_li]:leading-(--text-v3-chat--line-height) [&_p]:my-0 [&_p]:leading-(--text-v3-chat--line-height) [&_strong]:font-semibold [&_th]:font-semibold [&>p+p]:mt-3"
                >
                    {referenceTokensToMarkdown(text)}
                </Markdown>
                {presentations !== null && (
                    <div className="mt-2 space-y-2">{presentations}</div>
                )}
                {nextStepsOffer !== null &&
                    nextStepsOffer.answerOccurrenceId ===
                        occurrence.occurrenceId && (
                        <NextStepButtons
                            actions={nextStepsOffer.actions}
                            onSelect={nextStepsOffer.onSelect}
                        />
                    )}
                {!continuedByNext &&
                    followedByCard !== true &&
                    turnPosition?.kind !== 'interim' && (
                        <MessageActionRow
                            label="Phoebe response actions"
                            align="start"
                            visibility={actionsVisibility}
                        >
                            <AssistantMessageActions
                                occurrence={occurrence}
                                copyControl={
                                    <CopyMessageButton
                                        text={
                                            turnPosition?.kind === 'end'
                                                ? turnPosition.copyText
                                                : text
                                        }
                                    />
                                }
                            />
                            <time
                                className={cn(
                                    MESSAGE_TIME_CLASS,
                                    'ml-1 transition-opacity',
                                    REVEAL_ON_HOVER_CLASS,
                                )}
                                dateTime={
                                    occurrenceCreatedAt(occurrence) ?? undefined
                                }
                            >
                                {formattedTime}
                            </time>
                        </MessageActionRow>
                    )}
            </div>
        </article>
    );
}

export function UserTextMessage({
    text,
    occurrence,
    organizationTimeZone,
}: ChatTextMessageProps): ReactNode {
    const formattedTime = formatMessageTime(occurrence, organizationTimeZone);
    // Back-to-back messages from one person read as one: a follow-up drops
    // the repeated identity, and the hover-revealed time and copy action sit
    // under the group's last message only.
    const { continuesPrevious, continuedByNext } = useMessageContinuation();
    // Attribute only what the server resolved: `sent_by` is null whenever an
    // event has no enqueuing user, and until the viewer loads we cannot tell
    // "mine" from "theirs" — both cases keep the right-aligned default rather
    // than stamping a colleague's identity (or position) onto a message.
    const sender = occurrence.committed?.sent_by ?? null;
    const viewerUserId = useViewerUserId();
    const isTeammateMessage =
        sender !== null &&
        viewerUserId !== null &&
        sender.user_id !== viewerUserId;

    if (isTeammateMessage) {
        return (
            <article
                aria-label="User message"
                className={cn(
                    'group/message flex min-w-0 items-start gap-[9px]',
                    continuesPrevious && 'pl-[37px]',
                )}
            >
                {!continuesPrevious && (
                    <span
                        aria-hidden="true"
                        className={cn(
                            // Phoebe's chip shape with a per-user soft brand tone,
                            // so teammates read as the same system without
                            // borrowing Phoebe's signature purple.
                            'flex size-7 shrink-0 items-center justify-center rounded-md text-xs font-semibold',
                            getConversationAvatarToneClass(sender.user_id),
                        )}
                    >
                        {getAvatarInitials(sender.display_name)}
                    </span>
                )}
                <div className="w-full max-w-[65ch] min-w-0">
                    {!continuesPrevious && (
                        <div className="mb-1.5 flex items-baseline gap-[7px]">
                            <strong className="text-foreground text-v3-chat-meta font-[650]">
                                {sender.display_name}
                            </strong>
                        </div>
                    )}
                    <div className={USER_MESSAGE_BUBBLE_CLASSES}>
                        <ReferenceText text={text} attachedShifts={true} />
                    </div>
                    {!continuedByNext && (
                        <MessageActionRow
                            label="User message actions"
                            align="start"
                            visibility="hover"
                        >
                            <CopyMessageButton text={text} />
                            <time
                                className={cn(MESSAGE_TIME_CLASS, 'ml-1')}
                                dateTime={
                                    occurrenceCreatedAt(occurrence) ?? undefined
                                }
                            >
                                {formattedTime}
                            </time>
                        </MessageActionRow>
                    )}
                </div>
            </article>
        );
    }

    return (
        <article
            aria-label="User message"
            className="group/message flex min-w-0 flex-col items-end"
        >
            <div
                className={cn(
                    USER_MESSAGE_BUBBLE_CLASSES,
                    // Only the optimistic send rises in. Server-pending,
                    // reconciling, and rejected rows can outlive the send and
                    // remount on virtualizer scroll, so they must not replay
                    // the entrance — nor may the committed echo that replaces
                    // the optimistic row in place.
                    occurrence.phase === 'optimistic' && 'phoebe-chat-rise',
                )}
            >
                <ReferenceText text={text} attachedShifts={true} />
            </div>
            {!continuedByNext && (
                <MessageActionRow
                    label="User message actions"
                    align="end"
                    visibility="hover"
                >
                    <time
                        className={cn(MESSAGE_TIME_CLASS, 'mr-1')}
                        dateTime={occurrenceCreatedAt(occurrence) ?? undefined}
                    >
                        {formattedTime}
                    </time>
                    <CopyMessageButton text={text} />
                </MessageActionRow>
            )}
        </article>
    );
}
